/**
 * PaymentAccountsSection Component
 * Manages payment accounts (bank accounts) for an agency in the admin workflow
 * Designed to match the Oath dashboard and admin design language
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  BuildingLibraryIcon,
} from '@heroicons/react/24/outline';
import { OathButton } from '../../shared/OathUI';
import { getPaymentAccounts } from '../../../services/oathPaymentAccountService';
import PaymentAccountModal from './PaymentAccountModal';

// ============================================
// Summary Stat Pill
// ============================================
const StatPill = ({ label, value, variant = 'default', selected = false, onClick }) => {
  const variants = {
    default: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
    active: selected
      ? 'bg-green-200 dark:bg-green-800/50 text-green-800 dark:text-green-200 ring-2 ring-green-400 dark:ring-green-500'
      : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    inactive: selected
      ? 'bg-gray-300 dark:bg-gray-500 text-gray-900 dark:text-white ring-2 ring-gray-400 dark:ring-gray-400'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${variants[variant]}`}
    >
      <span>{label}</span>
      <span className="font-bold">{value}</span>
    </button>
  );
};

// ============================================
// Account Card - Grid-friendly
// ============================================
const ACCOUNT_TYPE_LABELS = {
  C: 'Checking',
  S: 'Savings',
  Checking: 'Checking',
  Savings: 'Savings',
};

const PaymentAccountCard = ({ account, onEdit, canManage, currentUserId }) => {
  const displayName = account.nickName || account.nameOnAccount || 'Unnamed Account';
  const subtitle = account.nickName && account.nameOnAccount ? account.nameOnAccount : null;
  const isOwnAccount = currentUserId && account.portalUserId === currentUserId;
  const isAgencyAccount = !account.portalUserId;

  return (
    <button
      type="button"
      onClick={() => canManage && onEdit ? onEdit(account) : undefined}
      className={`w-full text-left bg-white dark:bg-gray-800 rounded-xl shadow-md border transition-all duration-150 overflow-hidden group ${
      canManage && onEdit ? 'cursor-pointer' : ''
    } ${
      account.isActive
        ? 'border-l-2 border-l-primary border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 hover:shadow-lg hover:-translate-y-0.5'
        : 'border-l-2 border-l-gray-300 dark:border-l-gray-600 border-t-gray-200 border-r-gray-200 border-b-gray-200 dark:border-t-gray-700 dark:border-r-gray-700 dark:border-b-gray-700 opacity-70 hover:opacity-100'
    }`}>
      {/* Card Header */}
      <div className="p-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              account.isActive
                ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
            }`}>
              <BuildingLibraryIcon className="h-4.5 w-4.5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                {displayName}
              </h3>
              {subtitle && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{subtitle}</p>
              )}
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded ${
                  account.isActive
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {account.isActive ? 'Active' : 'Inactive'}
                </span>
                {isOwnAccount ? (
                  <span
                    title="Only visible to you"
                    className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 cursor-help"
                  >
                    Personal
                  </span>
                ) : isAgencyAccount ? (
                  <span
                    title="Visible to all authorized users in this agency"
                    className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 cursor-help"
                  >
                    Shared
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          {canManage && onEdit && (
            <PencilSquareIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 group-hover:text-primary dark:group-hover:text-primary-light transition-all duration-150 flex-shrink-0" />
          )}
        </div>
      </div>

      {/* Account Details Grid */}
      <div className="px-4 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Routing</p>
            <p className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300">
              {account.maskedRoutingNumber || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Account</p>
            <p className="text-xs font-mono font-medium text-gray-700 dark:text-gray-300">
              {account.maskedAccountNumber || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Type</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {ACCOUNT_TYPE_LABELS[account.accountType] || account.accountType || '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-0.5">Payment</p>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {account.defaultPaymentType || '—'}
            </p>
          </div>
        </div>
      </div>
    </button>
  );
};

// ============================================
// Loading Skeleton - Grid cards with shimmer
// ============================================
const ShimmerBar = ({ className }) => (
  <div className={`rounded bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 bg-[length:200%_100%] animate-shimmer ${className}`} />
);

const AccountGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="h-0.5 bg-gray-200 dark:bg-gray-700" />
        <div className="p-4 pb-3">
          <div className="flex items-start gap-3">
            <ShimmerBar className="w-9 h-9 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <ShimmerBar className="h-4 w-3/4" />
              <ShimmerBar className="h-3 w-1/2" />
            </div>
          </div>
        </div>
        <div className="px-4 pb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            {[1, 2, 3, 4].map((j) => (
              <div key={j} className="space-y-1.5">
                <ShimmerBar className="h-2.5 w-12" />
                <ShimmerBar className="h-3.5 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    ))}
  </div>
);

// ============================================
// Error State
// ============================================
const ErrorState = ({ message, onRetry }) => (
  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
    <ExclamationTriangleIcon className="h-10 w-10 text-red-400 dark:text-red-500 mx-auto mb-3" />
    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Failed to Load Accounts</h3>
    <p className="text-sm text-red-600 dark:text-red-400 mb-4">{message}</p>
    {onRetry && (
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
      >
        <ArrowPathIcon className="h-4 w-4" />
        Try Again
      </button>
    )}
  </div>
);

// ============================================
// Empty State
// ============================================
const EmptyState = ({ title, description, action }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-10 text-center">
    <div className="w-14 h-14 bg-primary/10 dark:bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
      <BanknotesIcon className="h-7 w-7 text-primary dark:text-primary-light" />
    </div>
    <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">{title}</h3>
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 max-w-sm mx-auto">{description}</p>
    {action}
  </div>
);

// ============================================
// Main Section Component
// ============================================

/**
 * PaymentAccountsSection Component
 *
 * @param {Object} agency - The agency object containing agencyId and agencyType
 * @param {number} currentUserId - The current user's portalUserId for ownership badges
 * @param {boolean} canManage - Whether the user has permission to add/edit accounts
 */
const PaymentAccountsSection = ({ agency, currentUserId, canManage = false }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // null = all, true = active, false = inactive
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const fetchAccounts = useCallback(async () => {
    if (!agency?.agencyId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getPaymentAccounts(agency.agencyId);
      if (result.success) {
        setAccounts(result.accounts || []);
      } else {
        setError(result.error || 'Failed to load payment accounts');
      }
    } catch (err) {
      setError('Failed to load payment accounts');
    } finally {
      setLoading(false);
    }
  }, [agency?.agencyId]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Derived stats
  const stats = useMemo(() => {
    const active = accounts.filter((a) => a.isActive).length;
    const inactive = accounts.length - active;
    return { total: accounts.length, active, inactive };
  }, [accounts]);

  // Filter accounts by search and active status
  const filteredAccounts = useMemo(() => {
    let filtered = accounts;
    if (activeFilter !== null) {
      filtered = filtered.filter((a) => a.isActive === activeFilter);
    }
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (account) =>
          account.nickName?.toLowerCase().includes(searchLower) ||
          account.nameOnAccount?.toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  }, [accounts, search, activeFilter]);

  const handleAddAccount = () => {
    setEditingAccount(null);
    setShowModal(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    setShowModal(false);
    setEditingAccount(null);
    fetchAccounts();
  };

  return (
    <div className="space-y-5">
      {/* Section Header with Stats, Search, and Add */}
      {!loading && accounts.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <StatPill label="Total" value={stats.total} selected={activeFilter === null} onClick={() => setActiveFilter(null)} />
            <StatPill label="Active" value={stats.active} variant="active" selected={activeFilter === true} onClick={() => setActiveFilter((f) => f === true ? null : true)} />
            {stats.inactive > 0 && (
              <StatPill label="Inactive" value={stats.inactive} variant="inactive" selected={activeFilter === false} onClick={() => setActiveFilter((f) => f === false ? null : false)} />
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative max-w-xs w-full sm:w-auto">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
            </div>
            {canManage && (
              <OathButton onClick={handleAddAccount} size="sm">
                <PlusIcon className="h-4 w-4" />
                Add Account
              </OathButton>
            )}
          </div>
        </div>
      )}
      {!loading && accounts.length === 0 && canManage && (
        <div className="flex justify-end">
          <OathButton onClick={handleAddAccount} size="sm">
            <PlusIcon className="h-4 w-4" />
            Add Account
          </OathButton>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <AccountGridSkeleton />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchAccounts} />
      ) : filteredAccounts.length === 0 ? (
        <EmptyState
          title={search ? 'No matching accounts' : 'No payment accounts found'}
          description={
            search
              ? 'Try adjusting your search term'
              : canManage
                ? 'No payment accounts found. Create one to get started.'
                : 'No payment accounts have been configured for this agency.'
          }
          action={
            !search && canManage ? (
              <OathButton onClick={handleAddAccount}>
                <PlusIcon className="h-4 w-4" />
                Add Payment Account
              </OathButton>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAccounts.map((account, i) => (
            <div key={account.accountId} className="animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
              <PaymentAccountCard
                account={account}
                onEdit={handleEditAccount}
                canManage={canManage}
                currentUserId={currentUserId}
              />
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <PaymentAccountModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setEditingAccount(null); }}
        onSuccess={handleModalSuccess}
        agencyId={agency?.agencyId}
        account={editingAccount}
      />
    </div>
  );
};

export default PaymentAccountsSection;
